import {
  Box,
  Button,
  CloseButton,
  Flex,
  Portal,
  Text,
  useToast,
} from '@chakra-ui/react';
import {
  hasInstalledMarker,
  readInstalledDisplayMode,
  usePwaStore,
} from '@diamond/sol-buyer-context';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import { detectIosDevice } from './canPromptWebPush';
import {
  getBannerBottomPx,
  getPageChromePx,
  useVisualViewportInset,
} from './pwa-install-banner-layout';
import {
  markTimedPrompt,
  PWA_INSTALL_PROMPT_KEY,
  shouldOfferTimedPromptFromStorage,
} from './timed-prompt';
import { usePwaUpdateNotification } from './usePwaUpdateNotification';

const INSTALL_TOAST_ID = 'pwa-install-guidance';
const IOS_INSTALL_TOAST =
  'Ketuk ikon Bagikan, lalu pilih "Tambah ke Layar Utama".';
const MAC_SAFARI_INSTALL_TOAST =
  'Buka menu File atau Bagikan, lalu pilih "Add to Dock".';

function readIosDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return detectIosDevice({
    maxTouchPoints: window.navigator.maxTouchPoints,
    platform: window.navigator.platform,
    userAgent: window.navigator.userAgent,
  });
}

function isMacSafari(): boolean {
  if (typeof window === 'undefined') return false;
  if (readIosDevice()) return false;
  const ua = window.navigator.userAgent;
  const isMacintosh = /Macintosh/.test(ua);
  const isSafari = /Safari/.test(ua);
  const isOtherEngine = /Chrome|Chromium|CriOS|Edg|OPR|Firefox/.test(ua);
  return isMacintosh && isSafari && !isOtherEngine;
}

type PwaInstallBannerProps = {
  isPwaEnabled: boolean;
};

export function PwaInstallBanner({ isPwaEnabled }: PwaInstallBannerProps) {
  const router = useRouter();
  const isInstallable = usePwaStore((state) => state.isInstallable);
  const isInstalled = usePwaStore((state) => state.isInstalled);
  const setIsInstallBannerVisible = usePwaStore(
    (state) => state.setIsInstallBannerVisible
  );
  const triggerInstallPrompt = usePwaStore(
    (state) => state.triggerInstallPrompt
  );
  const toast = useToast();
  const visualInsetPx = useVisualViewportInset();
  const pageChromePx = getPageChromePx(router.pathname);
  const bannerBottomPx = getBannerBottomPx(router.pathname, visualInsetPx);
  const sitsOnBottomNav = pageChromePx > 0;

  const [isDismissed, setIsDismissed] = useState(
    () => !shouldOfferTimedPromptFromStorage(PWA_INSTALL_PROMPT_KEY)
  );

  usePwaUpdateNotification(isPwaEnabled);

  const isStandalone =
    isInstalled ||
    hasInstalledMarker() ||
    (typeof window !== 'undefined' && readInstalledDisplayMode());
  const needsManualInstall = readIosDevice() || isMacSafari();
  const visible =
    isPwaEnabled &&
    !isDismissed &&
    !isStandalone &&
    (isInstallable || needsManualInstall);

  useEffect(() => {
    setIsInstallBannerVisible(visible);
    return () => {
      setIsInstallBannerVisible(false);
    };
  }, [setIsInstallBannerVisible, visible]);

  const showInstallGuidance = useCallback(
    (description: string) => {
      if (toast.isActive(INSTALL_TOAST_ID)) return;
      toast({
        id: INSTALL_TOAST_ID,
        position: 'top',
        duration: 8000,
        isClosable: true,
        containerStyle: { zIndex: 1700 },
        render: ({ onClose }) => (
          <Box bg="blue" color="white" p={4} borderRadius="md" boxShadow="md">
            <Flex justify="space-between" align="flex-start" gap={3}>
              <Box>
                <Text fontWeight="semibold">Pasang Aplikasi</Text>
                <Text fontSize="sm" mt={1}>
                  {description}
                </Text>
              </Box>
              <CloseButton color="white" onClick={onClose} />
            </Flex>
          </Box>
        ),
      });
    },
    [toast]
  );

  const handleInstall = useCallback(async () => {
    if (readIosDevice()) {
      markTimedPrompt(PWA_INSTALL_PROMPT_KEY);
      showInstallGuidance(IOS_INSTALL_TOAST);
      return;
    }

    if (isMacSafari()) {
      markTimedPrompt(PWA_INSTALL_PROMPT_KEY);
      showInstallGuidance(MAC_SAFARI_INSTALL_TOAST);
      return;
    }

    const outcome = await triggerInstallPrompt();
    if (outcome === 'accepted') {
      setIsDismissed(true);
    }
    if (outcome === 'dismissed') {
      markTimedPrompt(PWA_INSTALL_PROMPT_KEY);
    }
  }, [showInstallGuidance, triggerInstallPrompt]);

  const handleDismiss = useCallback(() => {
    markTimedPrompt(PWA_INSTALL_PROMPT_KEY);
    setIsDismissed(true);
  }, []);

  if (!visible) return null;

  return (
    <Portal>
      <Flex
        position="fixed"
        bottom={{
          base: sitsOnBottomNav
            ? `calc(${bannerBottomPx}px + env(safe-area-inset-bottom, 0px))`
            : `${bannerBottomPx}px`,
          md: `${visualInsetPx}px`,
        }}
        left={0}
        right={0}
        bg="white"
        borderTopWidth="1px"
        borderColor="gray.200"
        px={{ base: 3, md: 4 }}
        pt={3}
        pb={
          sitsOnBottomNav
            ? 3
            : {
                base: 'calc(env(safe-area-inset-bottom) + 0.75rem)',
                md: 3,
              }
        }
        direction={{ base: 'column', md: 'row' }}
        align={{ base: 'stretch', md: 'center' }}
        justify="space-between"
        gap={3}
        zIndex="overlay"
        boxShadow="0 -2px 10px rgba(0,0,0,0.1)"
      >
        <Flex align="center" gap={3} flex={1} minW={0}>
          <Text fontSize="xl" lineHeight={1}>
            &#11015;
          </Text>
          <Box minW={0}>
            <Text fontWeight="semibold" fontSize="sm">
              Pasang Sukanda OneLink
            </Text>
            <Text fontSize="xs" color="gray.500" overflowWrap="anywhere">
              Akses lebih cepat langsung dari layar utama
            </Text>
          </Box>
        </Flex>
        <Flex
          gap={2}
          ml={{ base: 0, md: 3 }}
          w={{ base: 'full', md: 'auto' }}
          minW={0}
        >
          <Button
            size="sm"
            variant="ghost"
            minH="44px"
            minW={0}
            flex={{ base: 1, md: 'initial' }}
            onClick={handleDismiss}
          >
            Nanti Saja
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            minH="44px"
            minW={0}
            flex={{ base: 1, md: 'initial' }}
            onClick={handleInstall}
          >
            Pasang
          </Button>
        </Flex>
      </Flex>
    </Portal>
  );
}
